import { Alert, Column, Skeleton, SkeletonBox } from "@hightouch/htui";
import { EmbedNotices } from "src/pages/ad-studio/insights/embed-notices";
import {
  useAdPerformanceTableEmbed,
  useTableEmbedPlan,
} from "src/pages/ad-studio/insights/use-ad-performance-table-embed";
import { FailureAlert } from "src/pages/ad-studio/insights/performance/failure-alert";
import type { EmbedPlan } from "src/pages/ad-studio/insights/performance/types";
import type { EmbedAttrs } from "src/pages/ad-studio/insights/performance/utils/embeds";
import { ListTable } from "src/pages/ad-studio/insights/list-table";
import { PerformanceTableSkeleton } from "src/pages/ad-studio/insights/performance/view/performance-table-skeleton";
import { PerformanceViewPrimitive } from "src/pages/ad-studio/insights/performance/view/performance-view-primitive";

type AdPerformanceTableEmbedProps = { attrs: EmbedAttrs };

const PlannedPerformanceTable = ({ plan }: { plan: EmbedPlan }) => {
  const { state, performAction } = useAdPerformanceTableEmbed({ plan });
  switch (state.status) {
    case "loading": {
      return <PerformanceTableSkeleton columns={state.planned} />;
    }
    case "failed": {
      return <FailureAlert failure={state.failure} />;
    }
    case "loaded": {
      const { page } = state;
      return (
        <PerformanceViewPrimitive.Root
          paging={page.paging}
          performAction={(action) =>
            performAction({ kind: "TableEmbedAction/paged", page: action.page })
          }
        >
          <PerformanceViewPrimitive.TableLayout>
            <ListTable
              page={page}
              performAction={(action) =>
                performAction({
                  kind: "TableEmbedAction/sorted-column",
                  key: action.key,
                })
              }
            />
            <PerformanceViewPrimitive.Pagination />
          </PerformanceViewPrimitive.TableLayout>
        </PerformanceViewPrimitive.Root>
      );
    }
    default: {
      return state satisfies never;
    }
  }
};

export const AdPerformanceTableEmbed = ({
  attrs,
}: AdPerformanceTableEmbedProps) => {
  const { state } = useTableEmbedPlan(attrs);
  switch (state.status) {
    case "loading": {
      return (
        <Skeleton isLoading>
          <SkeletonBox height={64} width="100%" borderRadius="md" />
        </Skeleton>
      );
    }
    case "failed": {
      return <FailureAlert failure={state.failure} />;
    }
    case "no-connected-channels": {
      return (
        <Alert
          type="info"
          title="No ad platforms are connected"
          message="Connect one to see its ads here."
        />
      );
    }
    case "planned": {
      return (
        <Column gap={2}>
          <EmbedNotices notices={state.plan.notices} />
          <PlannedPerformanceTable plan={state.plan} />
        </Column>
      );
    }
    default: {
      return state satisfies never;
    }
  }
};
